import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Translate, I18n } from 'react-redux-i18n';
import alertify from 'alertifyjs';
import { AngleDown, AngleUp } from 'theme/icon';
import * as actions from 'redux/actions/optionAlertActions';
import 'assets/css/alerts.css';

const PERCENT_MIN = 5;
const PERCENT_MAX = 100;
const PERCENT_STEP = 5;

function toNumber(value, fallback = 0) {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}

function resolveContractValue(item) {
    const openTrade = Math.abs(toNumber(item?.open_trade));
    if (openTrade > 0) {
        return openTrade;
    }
    const openPrice = Math.abs(toNumber(item?.open_price));
    const qty = Math.max(1, toNumber(item?.quantity, 1));
    return openPrice * qty * 100;
}

function formatMoney(value) {
    if (!Number.isFinite(value)) {
        return '—';
    }
    return value.toFixed(2);
}

function clampPercent(value) {
    const stepped = Math.round(toNumber(value, 50) / PERCENT_STEP) * PERCENT_STEP;
    return Math.min(PERCENT_MAX, Math.max(PERCENT_MIN, stepped));
}

const OptionAlertModal = (props) => {
    const { item, open, onClose } = props;
    const [percent, setPercent] = useState(50);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setPercent(50);
        }
    }, [open, item?.id]);

    const contractValue = useMemo(() => resolveContractValue(item), [item]);
    const profitValue = useMemo(
        () => contractValue * (toNumber(percent, 50) / 100),
        [contractValue, percent],
    );

    if (!open || !item) {
        return null;
    }

    const handleBackdropClick = (event) => {
        if (event.target === event.currentTarget) {
            onClose();
        }
    };

    const stepDown = () => {
        setPercent((prev) => clampPercent(prev - PERCENT_STEP));
    };

    const stepUp = () => {
        setPercent((prev) => clampPercent(prev + PERCENT_STEP));
    };

    const handleAdd = async () => {
        if (!props.user_id || !item.id) {
            return;
        }
        setSaving(true);
        const result = await props.newItem({
            id: props.user_id,
            trade_id: item.id,
            percent: Number(percent),
        }, props.token);
        setSaving(false);

        if (result?.success) {
            onClose();
            alertify.success(I18n.t('alert_saved'));
            return;
        }

        const errorKey = result?.error && I18n.t(result.error) !== result.error
            ? result.error
            : 'alert_save_failed';
        alertify.error(I18n.t(errorKey));
    };

    return (
        <div
            className="dialog stock-alert-dialog"
            role="presentation"
            onClick={handleBackdropClick}
        >
            <div
                className="dialog-content watchlist-picker-content stock-alert-dialog-content"
                role="dialog"
                aria-modal="true"
                aria-labelledby="option-alert-title"
                onClick={(e) => e.stopPropagation()}
            >
                <h2 id="option-alert-title" className="dialog-title">
                    <Translate value="option_alert_title" />
                </h2>
                <p className="dialog-content-text">
                    {String(item.symbol || '').toUpperCase()}
                    {item.strike != null ? ` ${item.strike}` : ''}
                    {item.option_kind ? ` ${item.option_kind}` : ''}
                    {item.market ? ` · ${item.market}` : ''}
                </p>

                <div className="stock-alert-modal-fields">
                    <div className="stock-alert-field">
                        <span className="stock-alert-field-label">
                            <Translate value="option_alert_profit" />
                        </span>
                        <div className="option-alert-percent-stepper">
                            <button
                                type="button"
                                className="option-alert-percent-btn"
                                onClick={stepDown}
                                disabled={percent <= PERCENT_MIN}
                                aria-label="Decrease percent"
                            >
                                <AngleDown />
                            </button>
                            <input
                                className="input option-alert-percent-input"
                                type="number"
                                value={percent}
                                readOnly
                                tabIndex={-1}
                                aria-live="polite"
                            />
                            <button
                                type="button"
                                className="option-alert-percent-btn"
                                onClick={stepUp}
                                disabled={percent >= PERCENT_MAX}
                                aria-label="Increase percent"
                            >
                                <AngleUp />
                            </button>
                        </div>
                    </div>

                    <div className="stock-alert-field">
                        <span className="stock-alert-field-label">
                            <Translate value="option_alert_contract_value" />
                        </span>
                        <div className="option-alert-calc-value">
                            ${formatMoney(contractValue)}
                        </div>
                    </div>

                    <div className="stock-alert-field">
                        <span className="stock-alert-field-label">
                            <Translate value="option_alert_profit_value" />
                        </span>
                        <div className="option-alert-calc-value option-alert-calc-value--profit">
                            ${formatMoney(profitValue)}
                            <span className="option-alert-calc-hint">
                                ({percent}%)
                            </span>
                        </div>
                    </div>
                </div>

                <div className="dialog-actions">
                    <button type="button" className="btn btn-secondary" onClick={onClose}>
                        <Translate value="close" />
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleAdd}
                        disabled={saving || contractValue <= 0}
                    >
                        <Translate value="alert_add" />
                    </button>
                </div>
            </div>
        </div>
    );
};

function mapStateToProps(reducer) {
    return {
        user_id: reducer.session.auth.id,
        token: reducer.session.auth.token,
    };
}

const mapDispatchToProps = (dispatch) => ({
    newItem: (params, token) => dispatch(actions.newItem({ params, token })),
});

export default connect(mapStateToProps, mapDispatchToProps)(OptionAlertModal);
